import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { KidAvatar } from "@/components/Avatars";

export const KIDS_RGB = "var(--glow-kids)";
const CONFETTI = ["var(--glow-kids)", "var(--glow-home)", "var(--glow-money)", "var(--glow-calendar)", "var(--glow-connect)", "var(--glow-family)"];

// Short confetti burst; parent clears it after ~1.8s.
export function Celebration({ emoji = "🎉", text = "Great job!", onDone, big = false, duration = 1800, testid = "celebration" }) {
  useEffect(() => {
    const t = setTimeout(() => onDone?.(), duration);
    return () => clearTimeout(t);
  }, [onDone, duration]);
  return (
    <div className={`pointer-events-none fixed inset-0 z-[150] flex items-center justify-center ${big ? "bg-black/30" : ""}`} data-testid={testid}>
      {Array.from({ length: big ? 90 : 36 }, (_, i) => (
        <span key={i} className="q-confetti absolute left-1/2 top-1/2 h-2.5 w-2 rounded-sm"
          style={{ background: `rgb(${CONFETTI[i % CONFETTI.length]})`, "--dx": `${Math.cos(i * 0.7) * (160 + (i % 5) * 50)}px`, "--dy": `${Math.sin(i * 0.7) * (120 + (i % 4) * 45) - 60}px`, "--rot": `${i * 47}deg`, animationDelay: `${(i % 6) * 25}ms` }} />
      ))}
      <div className={`q-pop rounded-3xl border border-q-line bg-q-surface text-center shadow-2xl ${big ? "mx-4 px-10 py-9" : "px-8 py-6"}`}>
        <div className={big ? "text-8xl" : "text-6xl"}>{emoji}</div>
        <p className={`mt-2 font-display font-extrabold text-q-text ${big ? "text-3xl sm:text-4xl" : "text-2xl"}`} data-testid={`${testid}-text`}>{text}</p>
      </div>
    </div>
  );
}

export const EMOJIS = ["⭐", "🛏️", "🐶", "🐱", "🧹", "🚗", "🧸", "🍽️", "♻️", "🪴", "📚", "🪥", "🧺", "🗑️", "🎒", "🎹", "⚽", "🚲", "🧽", "👕", "🥕", "🐟", "🏰", "🚙", "🛟", "🏊", "✈️", "🎁", "🏖️", "💻", "🎓", "🏠", "🎯", "💰"];

export function EmojiPicker({ value, onChange, testid = "emoji-picker" }) {
  return (
    <div className="flex flex-wrap gap-1.5" role="radiogroup" data-testid={testid}>
      {EMOJIS.map((e) => (
        <button key={e} type="button" role="radio" aria-checked={value === e} onClick={() => onChange(e)} data-testid={`${testid}-${e}`}
          className="inline-flex h-10 w-10 items-center justify-center rounded-lg border text-xl transition-[transform,border-color] duration-100 hover:scale-110"
          style={{ borderColor: value === e ? "rgb(var(--q-accent))" : "rgb(var(--q-border))", background: value === e ? "rgb(var(--q-accent) / 0.12)" : "rgb(var(--q-raised))" }}>
          {e}
        </button>
      ))}
    </div>
  );
}

export function KidTabs({ kids, value, onChange }) {
  return (
    <div className="mb-6 flex flex-wrap gap-2" data-testid="kid-tabs">
      {kids.map((k) => (
        <button key={k.id} onClick={() => onChange(k.id)} data-testid={`kid-tab-${k.name.toLowerCase()}`} data-active={value === k.id ? "1" : "0"}
          className="inline-flex min-h-[44px] items-center gap-2 rounded-full border px-3.5 py-1.5 text-sm font-bold transition-colors"
          style={{ borderColor: value === k.id ? `rgb(${KIDS_RGB})` : "rgb(var(--q-border))", background: value === k.id ? `rgba(${KIDS_RGB},0.14)` : "rgb(var(--q-surface))", color: "rgb(var(--q-text))" }}>
          <KidAvatar avatar={k.avatar} size={26} /> {k.name}
        </button>
      ))}
    </div>
  );
}

export function FormDialog({ open, onClose, title, description, children, testid }) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] w-[calc(100vw-1.5rem)] max-w-lg overflow-y-auto border-q-line bg-q-surface p-5 sm:p-6" data-testid={testid}>
        <DialogHeader>
          <DialogTitle className="font-display text-xl font-extrabold text-q-text">{title}</DialogTitle>
          {description && <DialogDescription className="text-q-muted">{description}</DialogDescription>}
        </DialogHeader>
        {children}
      </DialogContent>
    </Dialog>
  );
}

export function Confirm({ open, onClose, title, body, confirmLabel = "Delete", onConfirm, testid = "confirm-dialog" }) {
  return (
    <AlertDialog open={open} onOpenChange={(o) => !o && onClose()}>
      <AlertDialogContent className="w-[calc(100vw-1.5rem)] max-w-md border-q-line bg-q-surface" data-testid={testid}>
        <AlertDialogHeader>
          <AlertDialogTitle className="font-display text-q-text">{title}</AlertDialogTitle>
          {body && <AlertDialogDescription className="text-q-muted">{body}</AlertDialogDescription>}
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="border-q-line bg-q-raised text-q-text" data-testid={`${testid}-cancel`}>Cancel</AlertDialogCancel>
          <AlertDialogAction className="bg-red-600 text-white hover:bg-red-700" onClick={onConfirm} data-testid={`${testid}-confirm`}>{confirmLabel}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export function ProgressBar({ percent, rgb = KIDS_RGB, testid }) {
  return (
    <div className="h-2.5 overflow-hidden rounded-full bg-q-raised" data-testid={testid} role="progressbar" aria-valuenow={percent} aria-valuemin={0} aria-valuemax={100}>
      <div className="h-full rounded-full transition-[width] duration-500" style={{ width: `${Math.max(2, percent)}%`, background: `rgb(${rgb})` }} />
    </div>
  );
}

export function useBusy() {
  const [busy, setBusy] = useState(null);
  const run = async (key, fn) => {
    setBusy(key);
    try { return await fn(); } finally { setBusy(null); }
  };
  return [busy, run];
}
